import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import App from '../App';
import { loadStoredAuth } from '../authStorage';
import { hasFrontendPermission } from '../permissions';
import { AppShell } from '../layout/AppShell';
import { DashboardPage } from '../pages/DashboardPage';
import { ActivityPage } from '../pages/ActivityPage';
import { AnalyticsPage } from '../pages/AnalyticsPage';
import { CustomersPage } from '../pages/CustomersPage';
import { DispatchCommandCenterPage } from '../pages/DispatchCommandCenterPage';
import { InventoryPage } from '../pages/InventoryPage';
import { InvoicesPage } from '../pages/InvoicesPage';
import { JobsPage } from '../pages/JobsPage';
import { ProjectsPage } from '../pages/ProjectsPage';
import { QuotesPage } from '../pages/QuotesPage';
import { SchedulePage } from '../pages/SchedulePage';
import { SettingsPage } from '../pages/SettingsPage';
import { TeamPage } from '../pages/TeamPage';
import { UsersPage } from '../pages/UsersPage';
import { PlannerPage } from '../pages/PlannerPage';

function ShellAccessFallback({ permission }) {
  return (
    <section className="ui-state" role="status" aria-live="polite">
      <div className="ui-state-icon" aria-hidden="true">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 2.5 3 5.5v4.7c0 4.3 2.9 6.9 7 7.8 4.1-.9 7-3.5 7-7.8V5.5l-7-3Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M7.5 10 9 11.5l3.5-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h3>Access Restricted</h3>
      <p>{permission ? `Requires capability: ${permission}` : 'You do not have access to this page.'}</p>
    </section>
  );
}

function ShellRoute({ element, permission }) {
  const auth = loadStoredAuth();
  const user = auth?.user;
  const token = auth?.token;

  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  if (permission && !hasFrontendPermission(user, permission)) {
    return <ShellAccessFallback permission={permission} />;
  }

  return element;
}

export function AppRouter() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path="/dispatch" element={<ShellRoute element={<DispatchCommandCenterPage />} permission="dispatch.manage" />} />
        <Route path="/dashboard" element={<ShellRoute element={<DashboardPage />} />} />
        <Route path="/jobs" element={<ShellRoute element={<JobsPage />} permission="jobs.manage" />} />
        <Route path="/schedule" element={<ShellRoute element={<SchedulePage />} permission="dispatch.manage" />} />
        <Route path="/customers" element={<ShellRoute element={<CustomersPage />} permission="customers.manage" />} />
        <Route path="/activity" element={<ShellRoute element={<ActivityPage />} permission="customers.manage" />} />
        <Route path="/invoices" element={<ShellRoute element={<InvoicesPage />} permission="invoices.manage" />} />
        <Route path="/inventory" element={<ShellRoute element={<InventoryPage />} permission="inventory.manage" />} />
        <Route path="/projects" element={<ShellRoute element={<ProjectsPage />} permission="projects.manage" />} />
        <Route path="/project-planner" element={<ShellRoute element={<PlannerPage />} permission="projects.manage" />} />
        <Route path="/project-planner/:projectId" element={<ShellRoute element={<PlannerPage />} permission="projects.manage" />} />
        <Route path="/quotes" element={<ShellRoute element={<QuotesPage />} permission="quotes.manage" />} />
        <Route path="/team" element={<ShellRoute element={<TeamPage />} permission="technicians.manage" />} />
        <Route path="/users" element={<ShellRoute element={<UsersPage />} permission="accounts.manage" />} />
        <Route path="/analytics" element={<ShellRoute element={<AnalyticsPage />} permission="exports.view" />} />
        <Route path="/settings" element={<ShellRoute element={<SettingsPage />} permission="dispatch.manage" />} />
      </Route>

      <Route path="/*" element={<App />} />
    </Routes>
  );
}
